import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import './MerchantDashboard.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MerchantDashboard = () => {
  const navigate = useNavigate();
  const { user, sessionToken, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [merchantProfile, setMerchantProfile] = useState(null);
  const [brands, setBrands] = useState([]);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  useEffect(() => {
    loadMerchantData();
  }, []);

  const loadMerchantData = async () => {
    try {
      setLoading(true);
      
      // Check if user is already a merchant
      if (user?.is_merchant) {
        // Load merchant profile and brands
        const [profileRes, brandsRes] = await Promise.all([
          axios.get(`${API}/merchant/profile`, {
            headers: { Authorization: `Bearer ${sessionToken}` }
          }),
          axios.get(`${API}/merchant/brands`, {
            headers: { Authorization: `Bearer ${sessionToken}` }
          })
        ]);
        
        if (profileRes.data.success) {
          setMerchantProfile(profileRes.data.merchant_profile);
        }
        
        if (brandsRes.data.success) {
          setBrands(brandsRes.data.brands);
        }
      } else {
        setShowRegisterModal(true);
      }
    } catch (error) {
      console.error('Error loading merchant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAsMerchant = async (formData) => {
    try {
      const response = await axios.post(
        `${API}/merchant/register`,
        formData,
        {
          headers: { Authorization: `Bearer ${sessionToken}` }
        }
      );
      
      if (response.data.success || response.data.message === 'Already registered as merchant') {
        setShowRegisterModal(false);
        // Refresh user data to update is_merchant flag
        await refreshUser();
        // Reload merchant data
        loadMerchantData();
      }
    } catch (error) {
      console.error('Error registering as merchant:', error);
      alert('Failed to register as merchant');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="merchant-dashboard">
          <div className="loading">Loading merchant dashboard...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="merchant-dashboard">
        <div className="dashboard-header">
          <h1>🏪 Merchant Dashboard</h1>
          <p>Manage your brands, products, and inventory</p>
        </div>

        {merchantProfile && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon">🏢</div>
              <div className="stat-content">
                <h3>{brands.length}</h3>
                <p>Active Brands</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📦</div>
              <div className="stat-content">
                <h3>{merchantProfile.products_count || 0}</h3>
                <p>Total Products</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>{merchantProfile.verification_status}</h3>
                <p>Verification Status</p>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">💼</div>
              <div className="stat-content">
                <h3>{merchantProfile.subscription_plan}</h3>
                <p>Plan</p>
              </div>
            </div>
          </div>
        )}

        <div className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="actions-grid">
            <button 
              className="action-card"
              onClick={() => navigate('/merchant/brands')}
            >
              <span className="action-icon">🏪</span>
              <h3>Manage Brands</h3>
              <p>Create and edit your brands</p>
            </button>
            <button 
              className="action-card"
              onClick={() => navigate('/merchant/products')}
            >
              <span className="action-icon">📦</span>
              <h3>Manage Products</h3>
              <p>Add and edit your inventory</p>
            </button>
            <button 
              className="action-card highlight"
              onClick={() => navigate('/merchant/instore')}
            >
              <span className="action-icon">🏬</span>
              <h3>In-Store Mode</h3>
              <p>Virtual try-on for customers</p>
            </button>
            <button 
              className="action-card"
              onClick={() => navigate('/marketplace')}
            >
              <span className="action-icon">🛒</span>
              <h3>View Marketplace</h3>
              <p>See how customers see your brands</p>
            </button>
          </div>
        </div>

        <div className="brands-section">
          <div className="section-header">
            <h2>Your Brands</h2>
            <button 
              className="btn-primary"
              onClick={() => navigate('/merchant/brands')}
            >
              + Create Brand
            </button>
          </div>
          
          {brands.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🏪</div>
              <h3>No Brands Yet</h3>
              <p>Create your first brand to start selling</p>
              <button 
                className="btn-primary"
                onClick={() => navigate('/merchant/brands')}
              >
                Create Your First Brand
              </button>
            </div>
          ) : (
            <div className="brands-grid">
              {brands.map(brand => (
                <div key={brand.id} className="brand-card">
                  {brand.logo && (
                    <img src={brand.logo} alt={brand.name} className="brand-logo" />
                  )}
                  <h3>{brand.name}</h3>
                  <p className="brand-location">📍 {brand.city}, {brand.country}</p>
                  <p className="brand-products">{brand.product_count || 0} products</p>
                  <div className="brand-actions">
                    <button 
                      className="btn-secondary"
                      onClick={() => navigate(`/merchant/brands/${brand.id}`)}
                    >
                      Manage
                    </button>
                    <button 
                      className="btn-team"
                      onClick={() => navigate(`/merchant/brands/${brand.id}/team`)}
                    >
                      👥 Team
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {showRegisterModal && (
          <MerchantRegistrationModal
            onClose={() => setShowRegisterModal(false)}
            onRegister={handleRegisterAsMerchant}
          />
        )}
      </div>
    </Layout>
  );
};

const MerchantRegistrationModal = ({ onClose, onRegister }) => {
  const [formData, setFormData] = useState({
    business_name: '',
    business_type: 'retail',
    business_email: '',
    business_phone: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onRegister(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>Register as Merchant</h2>
        <p className="modal-subtitle">Start selling your products on FITTTY.AI</p>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Business Name *</label>
            <input
              type="text"
              value={formData.business_name}
              onChange={(e) => setFormData({...formData, business_name: e.target.value})}
              required
              placeholder="Your Fashion Boutique"
            />
          </div>
          
          <div className="form-group">
            <label>Business Type</label>
            <select
              value={formData.business_type}
              onChange={(e) => setFormData({...formData, business_type: e.target.value})}
            >
              <option value="retail">Retail Store</option>
              <option value="boutique">Boutique</option>
              <option value="designer">Designer Brand</option>
              <option value="wholesaler">Wholesaler</option>
            </select>
          </div>
          
          <div className="form-group">
            <label>Business Email</label>
            <input
              type="email"
              value={formData.business_email}
              onChange={(e) => setFormData({...formData, business_email: e.target.value})}
              placeholder="business@example.com"
            />
          </div>
          
          <div className="form-group">
            <label>Business Phone</label>
            <input
              type="tel"
              value={formData.business_phone}
              onChange={(e) => setFormData({...formData, business_phone: e.target.value})}
              placeholder="+1234567890"
            />
          </div>
          
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Register as Merchant
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MerchantDashboard;